/**
 * Páginas scaffold para los módulos del roadmap cableados en la nueva
 * navegación.  Cada una declara propósito, entregables y endpoints
 * previstos vía <ModuleScaffold>, de modo que toda la estructura de
 * navegación queda operativa y trazable sin páginas vacías.
 *
 * Las entidades nuevas de la Capa Operacional AI-First que ya disponen de
 * datos reales pre-computados (Valvula, PRV, Presion) se renderizan con sus
 * datos reales (`loadValvulas`/`loadPresion` de `api/realData.ts`).  Si los
 * JSON no están desplegados, los loaders devuelven `[]` y la página cae con
 * elegancia al <ModuleScaffold> de roadmap (degradación elegante, R17.4).
 */
import { useQuery } from "@tanstack/react-query";

import { PageHeader } from "../layout/PageHeader";
import {
  loadValvulas,
  loadPresion,
  type RealValvula,
  type RealPresion,
} from "../api/realData";
import { ModuleScaffold } from "./ModuleScaffold";

export function DmasPage(): JSX.Element {
  return (
    <ModuleScaffold
      eyebrow="Analítica NRW"
      title="DMAs"
      subtitle="Gestión y comparación de District Metered Areas."
      icon="users"
      proposito="Vista consolidada de cada DMA: balance, ILI, MNF, presión, km de red, clientes y evolución de NRW. Permite sectorizar, comparar DMAs y abrir step-tests nocturnos."
      entregables={[
        "Ficha por DMA con KPIs hidráulicos y económicos",
        "Comparador multi-DMA con ranking por ILI / MNF",
        "Step-test nocturno programable y seguimiento de MNF",
        "Vínculo directo a Casos Prioritarios y Mapa por DMA",
      ]}
      endpoints={["GET /dma", "GET /balance/dma/{id}", "GET /nrw?dma_id="]}
      relacionado={[
        { to: "/impacto", label: "Impacto Económico" },
        { to: "/mapa", label: "Mapa Operacional" },
      ]}
    />
  );
}

export function InsightsPage(): JSX.Element {
  return (
    <ModuleScaffold
      eyebrow="Analítica NRW"
      title="Insights"
      subtitle="Hallazgos automáticos generados por los modelos y reglas."
      icon="search"
      proposito="Feed de insights accionables: correlaciones detectadas, DMAs que empeorarán, clusters de fraude, anomalías emergentes. Cada insight enlaza a su evidencia y a la acción sugerida."
      entregables={[
        "Feed priorizado de insights con confianza del modelo",
        "Correlación espacio-temporal entre alertas",
        "Detección de DMAs en deterioro antes del umbral",
        "Conversión de insight a Caso Prioritario en un clic",
      ]}
      endpoints={["GET /insights", "GET /ml/models", "GET /casos"]}
      relacionado={[
        { to: "/prediccion", label: "Predicción" },
        { to: "/casos", label: "Casos Prioritarios" },
      ]}
    />
  );
}

export function TerrenoPage(): JSX.Element {
  return (
    <ModuleScaffold
      eyebrow="Operación"
      title="Terreno"
      subtitle="Vista de operación de campo y seguimiento en vivo."
      icon="droplet"
      proposito="Consola de operación de terreno: posición de cuadrillas en tiempo real, rutas optimizadas, órdenes en curso y captura de evidencia offline-first sincronizada desde la app móvil."
      entregables={[
        "Tracking en vivo de cuadrillas sobre el mapa",
        "Ruteo optimizado (VRP) por SLA y skills",
        "Sincronización offline-first de evidencia (fotos, GPS, firma)",
        "Estado de órdenes en ruta / en sitio / cerradas",
      ]}
      endpoints={["GET /cuadrillas", "GET /ordenes", "WS /terreno/live"]}
      relacionado={[
        { to: "/cuadrillas", label: "Cuadrillas" },
        { to: "/inspector/ordenes", label: "Órdenes" },
      ]}
    />
  );
}

export function SlaPage(): JSX.Element {
  return (
    <ModuleScaffold
      eyebrow="Operación"
      title="SLA"
      subtitle="Cumplimiento de plazos y escalamiento automático."
      icon="clock"
      proposito="Tablero de SLAs por severidad y tipo de orden: qué está por vencer, qué venció, escalamiento automático al supervisor y métricas de MTTR por cuadrilla."
      entregables={[
        "Tablero de SLA con semáforo por severidad",
        "Escalamiento automático al superar el umbral",
        "MTTR y tasa de cumplimiento por cuadrilla",
        "Alertas proactivas de SLA próximo a vencer",
      ]}
      endpoints={["GET /ordenes", "GET /sla/breaches", "POST /sla/escalate"]}
      relacionado={[{ to: "/inspector/ordenes", label: "Órdenes" }]}
    />
  );
}

export function EvidenciasPage(): JSX.Element {
  return (
    <ModuleScaffold
      eyebrow="Operación"
      title="Evidencias"
      subtitle="Repositorio firmado de evidencia de inspección."
      icon="shield"
      proposito="Galería de evidencia digital de cada inspección: fotos, video, firma, lecturas QR/NFC del medidor, con hash SHA-256 y manifiesto firmado para no repudio en disputas regulatorias."
      entregables={[
        "Galería de evidencia por orden con metadatos GPS/EXIF",
        "Verificación de integridad por hash firmado",
        "Trazabilidad inmutable replicada a WORM",
        "Exportación de paquete de evidencia para regulador",
      ]}
      endpoints={["GET /evidencias", "GET /ordenes/{id}/evidencia"]}
      relacionado={[
        { to: "/inspector/ordenes", label: "Órdenes" },
        { to: "/supervisor/auditoria", label: "Auditoría" },
      ]}
    />
  );
}

export function RedHidraulicaPage(): JSX.Element {
  return (
    <ModuleScaffold
      eyebrow="GIS & Red"
      title="Red Hidráulica"
      subtitle="Topología de tuberías, nodos y conectividad."
      icon="server"
      proposito="Modelo topológico de la red: tuberías por material/diámetro/edad, nodos, conectividad. Base para análisis de aislamiento de sectores y simulación hidráulica EPANET."
      entregables={[
        "Capa de tuberías con atributos (material, diámetro, edad)",
        "Análisis de aislamiento: qué clientes quedan sin servicio al cerrar una válvula",
        "Grafo de conectividad navegable",
        "Importación del modelo hacia el simulador EPANET",
      ]}
      endpoints={["GET /gis/red", "GET /gis/isolation?valve_id="]}
      relacionado={[
        { to: "/mapa", label: "Mapa Operacional" },
        { to: "/valvulas", label: "Válvulas" },
      ]}
    />
  );
}

/** Badge de estado de una Valvula. */
function valvulaEstadoBadge(estado: RealValvula["estado"]): JSX.Element {
  const cls =
    estado === "abierta"
      ? "badge badge--accent"
      : estado === "cerrada"
        ? "badge badge--danger"
        : "badge badge--warn";
  return <span className={cls}>{estado}</span>;
}

export function ValvulasPage(): JSX.Element {
  const q = useQuery<RealValvula[]>({
    queryKey: ["real.valvulas"],
    queryFn: () => loadValvulas(),
    staleTime: 60_000,
  });
  // Solo Valvulas (las PRV se muestran en su propia página).
  const valvulas = (q.data ?? []).filter((v) => v.tipo === "valvula");

  // Degradación elegante: sin datos reales → scaffold de roadmap.
  if (!q.isLoading && valvulas.length === 0) {
    return (
      <ModuleScaffold
        eyebrow="GIS & Red"
        title="Válvulas"
        subtitle="Inventario y estado de válvulas de la red."
        icon="settings"
        proposito="Inventario de válvulas con estado (abierta/cerrada/falla), maniobras históricas y análisis de aislamiento de sectores para cortes programados."
        entregables={[
          "Inventario georreferenciado de válvulas",
          "Estado y bitácora de maniobras",
          "Simulación de aislamiento de sector",
          "Órdenes de mantención de válvulas",
        ]}
        endpoints={["GET /gis/valvulas", "POST /gis/isolation/simulate"]}
        relacionado={[{ to: "/red", label: "Red Hidráulica" }]}
      />
    );
  }

  const abiertas = valvulas.filter((v) => v.estado === "abierta").length;
  const cerradas = valvulas.filter((v) => v.estado === "cerrada").length;
  const parciales = valvulas.filter((v) => v.estado === "parcial").length;

  return (
    <>
      <PageHeader
        eyebrow="GIS & Red"
        title="Válvulas"
        subtitle="Inventario georreferenciado y estado de las válvulas de la red de Antofagasta."
      />
      <div className="card-grid" style={{ marginBottom: 16 }}>
        <div className="kpi">
          <span className="kpi__label">Válvulas</span>
          <span className="kpi__value">{valvulas.length}</span>
          <span className="kpi__hint">inventario total</span>
        </div>
        <div className="kpi kpi--accent">
          <span className="kpi__label">Abiertas</span>
          <span className="kpi__value">{abiertas}</span>
          <span className="kpi__hint">flujo habilitado</span>
        </div>
        <div className="kpi kpi--danger">
          <span className="kpi__label">Cerradas</span>
          <span className="kpi__value">{cerradas}</span>
          <span className="kpi__hint">sector aislado</span>
        </div>
        <div className="kpi kpi--warn">
          <span className="kpi__label">Parciales</span>
          <span className="kpi__value">{parciales}</span>
          <span className="kpi__hint">maniobra intermedia</span>
        </div>
      </div>

      <section className="card">
        <div className="card__title">Inventario de válvulas</div>
        <div className="table-wrapper" style={{ borderColor: "transparent" }}>
          <table className="data-table">
            <thead>
              <tr>
                <th>Válvula</th>
                <th>DMA</th>
                <th>Estado</th>
                <th className="numeric">Diámetro (mm)</th>
                <th>Downstream</th>
                <th className="numeric">Lat</th>
                <th className="numeric">Lon</th>
              </tr>
            </thead>
            <tbody>
              {valvulas.map((v) => (
                <tr key={v.valvula_id}>
                  <td><strong>{v.valvula_id}</strong></td>
                  <td>{v.dma_nombre}</td>
                  <td>{valvulaEstadoBadge(v.estado)}</td>
                  <td className="numeric">{v.diametro_mm}</td>
                  <td>{v.downstream_dma_ref}</td>
                  <td className="numeric">{v.lat.toFixed(5)}</td>
                  <td className="numeric">{v.lon.toFixed(5)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </>
  );
}

export function PresionPage(): JSX.Element {
  const q = useQuery<RealPresion[]>({
    queryKey: ["real.presion"],
    queryFn: () => loadPresion(),
    staleTime: 60_000,
  });
  const puntos = q.data ?? [];

  // Degradación elegante: sin datos reales → scaffold de roadmap.
  if (!q.isLoading && puntos.length === 0) {
    return (
      <ModuleScaffold
        eyebrow="GIS & Red"
        title="Presión"
        subtitle="Monitoreo de presión y correlación con fugas."
        icon="droplet"
        proposito="Mapa de presiones (isobaras), detección de sobrepresión/subpresión vs setpoint, transientes (golpe de ariete) y correlación presión↔fugas por DMA."
        entregables={[
          "Mapa de isobaras interpolado por DMA",
          "Detección de transientes (ΔP/Δt)",
          "Correlación presión-fuga con reducción esperada",
          "Alertas de presión anómala vinculadas a PRVs",
        ]}
        endpoints={["GET /gis/presion", "GET /pressure/events"]}
        relacionado={[{ to: "/prvs", label: "PRVs" }]}
      />
    );
  }

  const avgPresion =
    puntos.reduce((acc, p) => acc + p.presion_mca, 0) / Math.max(1, puntos.length);
  const avgMnf =
    puntos.reduce((acc, p) => acc + p.mnf_lps, 0) / Math.max(1, puntos.length);
  const maxMnf = puntos.reduce((acc, p) => Math.max(acc, p.mnf_lps), 0);

  return (
    <>
      <PageHeader
        eyebrow="GIS & Red"
        title="Presión"
        subtitle="Presión y caudal mínimo nocturno (MNF) por sensor y DMA · red Antofagasta."
      />
      <div className="card-grid" style={{ marginBottom: 16 }}>
        <div className="kpi">
          <span className="kpi__label">Sensores</span>
          <span className="kpi__value">{puntos.length}</span>
          <span className="kpi__hint">puntos de presión</span>
        </div>
        <div className="kpi kpi--info">
          <span className="kpi__label">Presión media</span>
          <span className="kpi__value">{avgPresion.toFixed(1)}</span>
          <span className="kpi__hint">mca</span>
        </div>
        <div className="kpi kpi--accent">
          <span className="kpi__label">MNF medio</span>
          <span className="kpi__value">{avgMnf.toFixed(1)}</span>
          <span className="kpi__hint">l/s nocturno</span>
        </div>
        <div className="kpi kpi--warn">
          <span className="kpi__label">MNF máximo</span>
          <span className="kpi__value">{maxMnf.toFixed(1)}</span>
          <span className="kpi__hint">l/s · posible fuga</span>
        </div>
      </div>

      <section className="card">
        <div className="card__title">Mediciones de presión y MNF</div>
        <div className="table-wrapper" style={{ borderColor: "transparent" }}>
          <table className="data-table">
            <thead>
              <tr>
                <th>Sensor</th>
                <th>DMA</th>
                <th className="numeric">Presión (mca)</th>
                <th className="numeric">Presión nocturna (mca)</th>
                <th className="numeric">MNF (l/s)</th>
                <th>Timestamp</th>
              </tr>
            </thead>
            <tbody>
              {puntos.map((p) => (
                <tr key={p.sensor_id}>
                  <td><strong>{p.sensor_id}</strong></td>
                  <td>{p.dma_ref}</td>
                  <td className="numeric">{p.presion_mca.toFixed(1)}</td>
                  <td className="numeric">{p.presion_nocturna_mca.toFixed(1)}</td>
                  <td className="numeric">{p.mnf_lps.toFixed(2)}</td>
                  <td>{new Date(p.ts).toLocaleString("es-CL")}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </>
  );
}

export function PrvsPage(): JSX.Element {
  const q = useQuery<RealValvula[]>({
    queryKey: ["real.valvulas"],
    queryFn: () => loadValvulas(),
    staleTime: 60_000,
  });
  // Las PRV son válvulas con tipo "PRV" y setpoint configurable.
  const prvs = (q.data ?? []).filter((v) => v.tipo === "PRV");

  // Degradación elegante: sin datos reales → scaffold de roadmap.
  if (!q.isLoading && prvs.length === 0) {
    return (
      <ModuleScaffold
        eyebrow="GIS & Red"
        title="PRVs"
        subtitle="Gestión de válvulas reductoras de presión."
        icon="settings"
        proposito="Gestión de PRVs: setpoints diurno/nocturno, historial de ajustes auditado y correlación posterior con la reducción de NRW y MNF nocturno."
        entregables={[
          "Inventario de PRVs con setpoints activos",
          "Programación de bajada de presión nocturna",
          "Auditoría de cambios de setpoint",
          "Impacto medido en MNF tras cada ajuste",
        ]}
        endpoints={["GET /prvs", "POST /prvs/{id}/setpoint"]}
        relacionado={[{ to: "/presion", label: "Presión" }]}
      />
    );
  }

  const setpoints = prvs
    .map((p) => p.setpoint_mca)
    .filter((s): s is number => s != null);
  const avgSetpoint =
    setpoints.reduce((acc, s) => acc + s, 0) / Math.max(1, setpoints.length);
  const activas = prvs.filter((p) => p.estado !== "cerrada").length;

  return (
    <>
      <PageHeader
        eyebrow="GIS & Red"
        title="PRVs"
        subtitle="Válvulas reductoras de presión con setpoint configurable · red Antofagasta."
      />
      <div className="card-grid" style={{ marginBottom: 16 }}>
        <div className="kpi">
          <span className="kpi__label">PRVs</span>
          <span className="kpi__value">{prvs.length}</span>
          <span className="kpi__hint">inventario total</span>
        </div>
        <div className="kpi kpi--accent">
          <span className="kpi__label">Activas</span>
          <span className="kpi__value">{activas}</span>
          <span className="kpi__hint">regulando presión</span>
        </div>
        <div className="kpi kpi--info">
          <span className="kpi__label">Setpoint medio</span>
          <span className="kpi__value">{avgSetpoint.toFixed(1)}</span>
          <span className="kpi__hint">mca</span>
        </div>
      </div>

      <section className="card">
        <div className="card__title">Inventario de PRVs</div>
        <div className="table-wrapper" style={{ borderColor: "transparent" }}>
          <table className="data-table">
            <thead>
              <tr>
                <th>PRV</th>
                <th>DMA</th>
                <th>Estado</th>
                <th className="numeric">Setpoint (mca)</th>
                <th className="numeric">Diámetro (mm)</th>
                <th>Downstream</th>
              </tr>
            </thead>
            <tbody>
              {prvs.map((p) => (
                <tr key={p.valvula_id}>
                  <td><strong>{p.valvula_id}</strong></td>
                  <td>{p.dma_nombre}</td>
                  <td>{valvulaEstadoBadge(p.estado)}</td>
                  <td className="numeric">
                    {p.setpoint_mca != null ? p.setpoint_mca.toFixed(1) : "—"}
                  </td>
                  <td className="numeric">{p.diametro_mm}</td>
                  <td>{p.downstream_dma_ref}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </>
  );
}

export function CoberturaPage(): JSX.Element {
  return (
    <ModuleScaffold
      eyebrow="GIS & Red"
      title="Cobertura IoT"
      subtitle="Cobertura RF de la red LoRaWAN."
      icon="wifi"
      proposito="Mapa de cobertura RF de los gateways LoRaWAN: zonas con señal débil, medidores huérfanos sin gateway cercano y planificación de nuevos gateways."
      entregables={[
        "Heatmap de cobertura RF (RSSI/SNR)",
        "Medidores sin cobertura adecuada",
        "Planificación de nuevos gateways",
        "Vínculo con Observabilidad para salud de red",
      ]}
      endpoints={["GET /gis/cobertura", "GET /observability"]}
      relacionado={[{ to: "/observabilidad", label: "Observabilidad" }]}
    />
  );
}

export function HeatmapsPage(): JSX.Element {
  return (
    <ModuleScaffold
      eyebrow="GIS & Red"
      title="Heatmaps"
      subtitle="Mapas de calor de pérdidas, fugas y consumo."
      icon="search"
      proposito="Capas de densidad: fugas históricas, pérdida estimada, consumo, reincidencia de fraude. Permite identificar zonas calientes y priorizar barridos."
      entregables={[
        "Heatmap de fugas históricas y reincidencia",
        "Densidad de pérdida estimada (m³/km)",
        "Playback temporal de evolución de zonas calientes",
        "Cruce con Casos Prioritarios",
      ]}
      endpoints={["GET /gis/heatmap?layer=", "GET /gis/features"]}
      relacionado={[{ to: "/mapa", label: "Mapa Operacional" }]}
    />
  );
}

export function IntegracionesPage(): JSX.Element {
  return (
    <ModuleScaffold
      eyebrow="Plataforma"
      title="Integraciones"
      subtitle="Conectores con sistemas externos."
      icon="server"
      proposito="Gestión de conectores: AMI/LoRaWAN Network Server, IoT Core, facturación comercial, SCADA, GIS corporativo y webhooks salientes firmados."
      entregables={[
        "Catálogo de conectores con estado de salud",
        "Configuración de webhooks salientes (HMAC)",
        "Mapeo de campos entrante/saliente",
        "Bitácora de sincronización y reintentos",
      ]}
      endpoints={["GET /integraciones", "POST /integraciones/{id}/test"]}
      relacionado={[{ to: "/observabilidad", label: "Observabilidad" }]}
    />
  );
}

export function UsuariosPage(): JSX.Element {
  return (
    <ModuleScaffold
      eyebrow="Plataforma"
      title="Usuarios & RBAC"
      subtitle="Gestión de usuarios, roles y permisos."
      icon="users"
      proposito="Administración de identidades y matriz RBAC + ABAC versionada: roles, recursos, acciones y reglas por atributo (ej. inspector solo ve sus órdenes, operador solo sus DMAs)."
      entregables={[
        "Gestión de usuarios federados vía OIDC",
        "Matriz RBAC editable y versionada",
        "Reglas ABAC por atributo (DMA, asignación)",
        "Auditoría de cambios de permisos",
      ]}
      endpoints={["GET /usuarios", "GET /rbac/policy", "POST /rbac/policy"]}
      relacionado={[{ to: "/supervisor/auditoria", label: "Auditoría" }]}
    />
  );
}
